import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import {
	AIDisclaimer,
	AIFeedbackComponent,
	AIFeedbackPropertiesProvider,
	AILabsLozenge,
} from '@atlassian/ai-components';

import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';

const AUDIO_FEEDBACK_ENTRYPOINT_ID = 'ffb1f355-3d3c-45c7-a0d1-1f13eb13baa3';

const styles = cssMap({
	footerWrapper: {
		paddingInline: token('space.100'),
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.075'),
	},
	footerContainer: {
		position: 'absolute',
		left: token('space.150'),
		right: token('space.150'),
		bottom: token('space.500'),
		paddingBlockEnd: token('space.050'),
	},
});

type AudioFeedbackFooterProps = {
	userId?: string;
	displayName?: string;
};

export const AudioFeedbackFooter = ({
	userId,
	displayName,
}: AudioFeedbackFooterProps): React.JSX.Element => {
	const { activeContent, activeFeature } = useAudioPlayerStore();
	const { trackAIFeedbackSubmit } = useAIEventsInstrumentation();

	const handleLikeClicked = useCallback(() => {
		trackAIFeedbackSubmit('up');
	}, [trackAIFeedbackSubmit]);

	const handleDislikeClicked = useCallback(() => {
		trackAIFeedbackSubmit('down');
	}, [trackAIFeedbackSubmit]);

	return (
		<FlagsProvider>
			<AIFeedbackPropertiesProvider
				entrypointId={AUDIO_FEEDBACK_ENTRYPOINT_ID}
				userId={userId ?? undefined}
				displayName={displayName ?? undefined}
				feedbackContext={{
					feature: activeFeature,
					isTeamEUVersion: true,
				}}
			>
				<Box xcss={styles.footerContainer}>
					<Inline
						spread="space-between"
						grow="fill"
						alignBlock="start"
						alignInline="center"
						xcss={styles.footerWrapper}
					>
						<Inline alignBlock="center" space="space.050">
							{FeatureGates.getExperimentValue(
								'confluence_audio_labs_lozenge',
								'isEnabled',
								false,
							) && <AILabsLozenge />}
							<AIDisclaimer withLink={true} />
						</Inline>
						<AIFeedbackComponent
							contentId={activeContent.contentId}
							onLikeClicked={handleLikeClicked}
							onDislikeClicked={handleDislikeClicked}
						/>
					</Inline>
				</Box>
			</AIFeedbackPropertiesProvider>
		</FlagsProvider>
	);
};
